import {computed, reactive, ref, watch} from 'vue';
import {createRule} from '@regle/core';
import {required} from '@regle/rules';
import {useRouter} from '@typed-router';
import {storeToRefs} from 'pinia';
import {useProfilePageViewMode, useRequestURL, useTranslation} from '#imports';
import {copyToClipboard} from '@malt/dom';
import {useRegle} from '@malt/forms';
import type {Maybe} from '@malt/types-utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {useProfileTracking} from '~/composables/useProfileTracking';
import {useProfileStore} from '~/store/profileStore';

export function useShareProfile() {
    const {t} = useTranslation();
    const profileStore = useProfileStore();
    const {updateSeoUrl} = profileStore;
    const {profile} = storeToRefs(profileStore);
    const {isEditionEnabled} = useProfilePageViewMode();
    const router = useRouter();
    const requestUrl = useRequestURL();
    const {trackProfileSocialShareStarted} = useProfileTracking();

    // refs a-z
    const currentProfileSeoAlias = ref<string>(profile.value?.seoAlias || '');
    const errorSeoAlias = ref<string | undefined>(undefined);
    const isEditingSeoAlias = ref<boolean>(false);
    const isSavingSeoAlias = ref<boolean>(false);

    // default: show full URL
    const state = reactive({
        seoAlias: getProfileUrl(profile.value?.seoAlias || ''),
    });

    // computed
    const profileLink = computed((): string => getProfileUrl(currentProfileSeoAlias.value));

    // validation
    const validSeoAlias = createRule({
        validator: (value: Maybe<string>) => /^[A-Za-z0-9]+-?[A-Za-z0-9]+$/.test(value || ''),
        message: () => t('profile.resource.bad.parameter.seo.url.pattern'),
    });
    const {r$} = useRegle(state, {
        seoAlias: {required, validSeoAlias},
    });

    watch(isEditingSeoAlias, (newValue) => {
        state.seoAlias = newValue ? currentProfileSeoAlias.value : getProfileUrl(currentProfileSeoAlias.value);
    });

    function getProfileUrl(seoAlias: string): string {
        return `${requestUrl.origin}/profile/${seoAlias}`;
    }

    function handleClickOnCopy() {
        if (!profileLink.value) return;
        copyToClipboard(profileLink.value);
        pushVJoySnackbar({props: {message: t('copy.to.clipboard.message')}});
        trackProfileSocialShareStarted('direct_link', profile.value?.accountId || '', isEditionEnabled.value);
    }

    async function handleClickOnSave(event: Event) {
        const {valid} = await r$.$validate();
        if (!valid) return;

        isSavingSeoAlias.value = true;
        errorSeoAlias.value = undefined;

        try {
            await updateSeoUrl({url: state.seoAlias, urlWithoutInvariant: ''});
            currentProfileSeoAlias.value = state.seoAlias;
            isEditingSeoAlias.value = false;
            pushVJoySnackbar({props: {message: t('account.update.success')}});
            (event.target as HTMLElement)?.blur();
        } catch (error: any) {
            const details = error?.response?._data?.details;
            if (details?.length) {
                errorSeoAlias.value = details[0].error;
            } else {
                pushVJoySnackbar({props: {message: t('profile.creation.common.notification.error.technical'), level: 'error'}});
            }
        }

        isSavingSeoAlias.value = false;
    }

    function handleSeoAliasInputFocus() {
        isEditingSeoAlias.value = true;
        errorSeoAlias.value = undefined;
    }

    function handleSeoAliasInputUpdate(newSeoAlias: string) {
        state.seoAlias = newSeoAlias;
    }

    function resetState() {
        isEditingSeoAlias.value = false;
        isSavingSeoAlias.value = false;
        errorSeoAlias.value = undefined;
        r$.$reset();
    }

    function handleSeoDialogHide() {
        resetState();
        router.push(currentProfileSeoAlias.value);
    }

    return {
        // refs a-z
        currentProfileSeoAlias,
        errorSeoAlias,
        isEditingSeoAlias,
        isSavingSeoAlias,
        state,
        // computed a-z
        profileLink,
        // validation
        r$,
        // functions a-z
        handleClickOnCopy,
        handleClickOnSave,
        handleSeoAliasInputFocus,
        handleSeoAliasInputUpdate,
        handleSeoDialogHide,
        resetState,
    };
}
